// src/pages/Overview.js
import React, { useState, useEffect } from 'react';
import {
  getAccount,
  getPositions,
  getPortfolioHistory,
  getMultiStrategySummary,
  STRATEGY_NAMES,
  getSymbolStrategy
} from '../services/api';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import './Overview.css';

const Overview = () => {
  const [account, setAccount] = useState(null);
  const [positions, setPositions] = useState([]);
  const [strategyData, setStrategyData] = useState(null);
  const [selectedStrategy, setSelectedStrategy] = useState('all');
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadData();
    const interval = setInterval(loadData, 30000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadData = async () => {
    try {
      const [accountData, positionsData, multiStratData] = await Promise.all([
        getAccount().catch(err => {
          console.error('Account error:', err);
          return null;
        }),
        getPositions().catch(err => {
          console.error('Positions error:', err);
          return { positions: [] };
        }),
        getMultiStrategySummary().catch(err => {
          console.error('Multi-strategy summary error:', err);
          return null;
        })
      ]);
      
      setAccount(accountData);
      const positionsList = positionsData?.positions || [];
      setPositions(positionsList);

      // Compute per-strategy position stats client-side using getSymbolStrategy,
      // so they stay consistent with the Positions tab. Override only the fields
      // that depend on position classification; keep allocation/expected_return from backend.
      if (multiStratData) {
        const computedStats = ['vwap', 'meanrev', 'momentum'].reduce((acc, strat) => {
          const stratPositions = positionsList.filter(p => getSymbolStrategy(p.symbol) === strat);
          acc[strat] = {
            ...multiStratData.strategies[strat],
            positions_count: stratPositions.length,
            total_value: stratPositions.reduce((s, p) => s + p.market_value, 0),
            unrealized_pnl: stratPositions.reduce((s, p) => s + p.unrealized_pl, 0),
          };
          return acc;
        }, {});
        setStrategyData({ ...multiStratData, strategies: computedStats });
      } else {
        setStrategyData(null);
      }
      
      try {
        const historyData = await getPortfolioHistory('1M');
        const chartData = historyData.timestamp.map((t, i) => ({
          date: new Date(t * 1000).toLocaleDateString(),
          equity: historyData.equity[i],
          pnl: historyData.profit_loss[i]
        }));
        setHistory(chartData);
      } catch (historyError) {
        console.warn('Portfolio history not available:', historyError);
        setHistory([]);
      }
      
      setLoading(false);
    } catch (error) {
      console.error('Error loading overview data:', error);
      setLoading(false);
    }
  };

  if (loading) {
    return <div className="loading">Loading portfolio data...</div>;
  }

  const totalPnL = account ? account.equity - account.last_equity : 0;
  const totalPnLPercent = account && account.last_equity > 0 
    ? ((totalPnL / account.last_equity) * 100) 
    : 0;

  // Filter positions by strategy
  const filteredPositions = selectedStrategy === 'all'
    ? positions
    : positions.filter(pos => getSymbolStrategy(pos.symbol) === selectedStrategy);

  // Strategy colors
  const strategyColors = {
    vwap: '#00d4ff',
    meanrev: '#00ff88',
    momentum: '#ffd93d'
  };

  // Calculate filtered metrics
  const getFilteredMetrics = () => {
    if (selectedStrategy === 'all') {
      return {
        totalValue: account ? account.portfolio_value : 0,
        cash: account ? account.cash : 0,
        buyingPower: account ? account.buying_power : 0,
        positionCount: positions.length,
        positionValue: positions.reduce((sum, pos) => sum + pos.market_value, 0),
        totalPnL: totalPnL,
        totalPnLPercent: totalPnLPercent
      };
    }
    
    // Filtered by strategy
    const stratPositionValue = filteredPositions.reduce((sum, pos) => sum + pos.market_value, 0);
    const stratPositionPnL = filteredPositions.reduce((sum, pos) => sum + pos.unrealized_pl, 0);
    const stratPositionPnLPercent = stratPositionValue > 0 
      ? (stratPositionPnL / (stratPositionValue - stratPositionPnL)) * 100 
      : 0;
    
    // Get strategy allocation from strategyData if available
    const stratAllocation = strategyData && strategyData.strategies[selectedStrategy]
      ? strategyData.strategies[selectedStrategy].allocation_value
      : 0;
    
    return {
      totalValue: stratPositionValue,
      cash: account ? account.cash : 0,
      buyingPower: stratAllocation,
      positionCount: filteredPositions.length,
      positionValue: stratPositionValue,
      totalPnL: stratPositionPnL,
      totalPnLPercent: stratPositionPnLPercent
    };
  };

  const metrics = getFilteredMetrics();

  return (
    <div className="overview">
      <div className="overview-header">
        <h1>Portfolio Overview</h1>
        
        {/* Strategy Filter */}
        <div className="strategy-selector">
          <label>View:</label>
          <select 
            value={selectedStrategy} 
            onChange={(e) => setSelectedStrategy(e.target.value)}
            className="strategy-dropdown"
          >
            <option value="all">{STRATEGY_NAMES.all}</option>
            <option value="vwap">{STRATEGY_NAMES.vwap}</option>
            <option value="meanrev">{STRATEGY_NAMES.meanrev}</option>
            <option value="momentum">{STRATEGY_NAMES.momentum}</option>
          </select>
        </div>
      </div>
      
      {/* Account Stats */}
      <div className="stats-grid">
        <div className="stat-card">
          <div className="stat-label">
            {selectedStrategy === 'all' ? 'Portfolio Value' : 'Position Value'}
          </div>
          <div className="stat-value">
            ${metrics.totalValue.toLocaleString('en-US', {minimumFractionDigits: 2, maximumFractionDigits: 2})}
          </div>
          <div className={`stat-change ${metrics.totalPnL >= 0 ? 'positive' : 'negative'}`}>
            {metrics.totalPnL >= 0 ? '+' : ''}${metrics.totalPnL.toFixed(2)} ({metrics.totalPnLPercent >= 0 ? '+' : ''}{metrics.totalPnLPercent.toFixed(2)}%)
          </div>
          {selectedStrategy !== 'all' && (
            <div className="stat-subtext" style={{color: strategyColors[selectedStrategy]}}>
              {STRATEGY_NAMES[selectedStrategy]}
            </div>
          )}
        </div>

        <div className="stat-card">
          <div className="stat-label">Cash</div>
          <div className="stat-value">
            ${metrics.cash.toLocaleString('en-US', {minimumFractionDigits: 2, maximumFractionDigits: 2})}
          </div>
          {selectedStrategy !== 'all' && (
            <div className="stat-subtext">Total account cash</div>
          )}
        </div>

        <div className="stat-card">
          <div className="stat-label">
            {selectedStrategy === 'all' ? 'Buying Power' : 'Allocated Capital'}
          </div>
          <div className="stat-value">
            ${metrics.buyingPower.toLocaleString('en-US', {minimumFractionDigits: 2, maximumFractionDigits: 2})}
          </div>
          {selectedStrategy !== 'all' && strategyData && (
            <div className="stat-subtext">
              {strategyData.strategies[selectedStrategy].allocation_pct}% allocation
            </div>
          )}
        </div>

        <div className="stat-card">
          <div className="stat-label">Open Positions</div>
          <div className="stat-value">{metrics.positionCount}</div>
          {selectedStrategy !== 'all' && (
            <div className="stat-subtext" style={{color: strategyColors[selectedStrategy]}}>
              {STRATEGY_NAMES[selectedStrategy]}
            </div>
          )}
        </div>
      </div>

      {/* Multi-Strategy Breakdown */}
      {strategyData && (
        <div className="strategy-breakdown">
          <h2>Strategy Allocation & Performance</h2>
          
          <div className="strategy-grid">
            {/* VWAP Strategy */}
            <div className="strategy-card" style={{borderTop: `3px solid ${strategyColors.vwap}`}}>
              <div className="strategy-header">
                <h3 style={{color: strategyColors.vwap}}>{strategyData.strategies.vwap.name}</h3>
                <div className="strategy-badge">{strategyData.strategies.vwap.allocation_pct}% Allocation</div>
              </div>
              <div className="strategy-stats">
                <div className="strategy-stat">
                  <span className="stat-label">Allocated Capital</span>
                  <span className="stat-value">
                    ${strategyData.strategies.vwap.allocation_value.toLocaleString('en-US', {minimumFractionDigits: 2})}
                  </span>
                </div>
                <div className="strategy-stat">
                  <span className="stat-label">Positions</span>
                  <span className="stat-value">{strategyData.strategies.vwap.positions_count}</span>
                </div>
                <div className="strategy-stat">
                  <span className="stat-label">Current Value</span>
                  <span className="stat-value">
                    ${strategyData.strategies.vwap.total_value.toLocaleString('en-US', {minimumFractionDigits: 2})}
                  </span>
                </div>
                <div className="strategy-stat">
                  <span className="stat-label">Unrealized P&L</span>
                  <span className={`stat-value ${strategyData.strategies.vwap.unrealized_pnl >= 0 ? 'positive' : 'negative'}`}>
                    {strategyData.strategies.vwap.unrealized_pnl >= 0 ? '+' : ''}
                    ${strategyData.strategies.vwap.unrealized_pnl.toFixed(2)}
                  </span>
                </div>
              </div>
            </div>

            {/* Mean Reversion Strategy */}
            <div className="strategy-card" style={{borderTop: `3px solid ${strategyColors.meanrev}`}}>
              <div className="strategy-header">
                <h3 style={{color: strategyColors.meanrev}}>{strategyData.strategies.meanrev.name}</h3>
                <div className="strategy-badge">{strategyData.strategies.meanrev.allocation_pct}% Allocation</div>
              </div>
              <div className="strategy-stats">
                <div className="strategy-stat">
                  <span className="stat-label">Allocated Capital</span>
                  <span className="stat-value">
                    ${strategyData.strategies.meanrev.allocation_value.toLocaleString('en-US', {minimumFractionDigits: 2})}
                  </span>
                </div>
                <div className="strategy-stat">
                  <span className="stat-label">Positions</span>
                  <span className="stat-value">{strategyData.strategies.meanrev.positions_count}</span>
                </div>
                <div className="strategy-stat">
                  <span className="stat-label">Current Value</span>
                  <span className="stat-value">
                    ${strategyData.strategies.meanrev.total_value.toLocaleString('en-US', {minimumFractionDigits: 2})}
                  </span>
                </div>
                <div className="strategy-stat">
                  <span className="stat-label">Unrealized P&L</span>
                  <span className={`stat-value ${strategyData.strategies.meanrev.unrealized_pnl >= 0 ? 'positive' : 'negative'}`}>
                    {strategyData.strategies.meanrev.unrealized_pnl >= 0 ? '+' : ''}
                    ${strategyData.strategies.meanrev.unrealized_pnl.toFixed(2)}
                  </span>
                </div>
              </div>
            </div>

            {/* Momentum Strategy */}
            <div className="strategy-card" style={{borderTop: `3px solid ${strategyColors.momentum}`}}>
              <div className="strategy-header">
                <h3 style={{color: strategyColors.momentum}}>{strategyData.strategies.momentum.name}</h3>
                <div className="strategy-badge">{strategyData.strategies.momentum.allocation_pct}% Allocation</div>
              </div>
              <div className="strategy-stats">
                <div className="strategy-stat">
                  <span className="stat-label">Allocated Capital</span>
                  <span className="stat-value">
                    ${strategyData.strategies.momentum.allocation_value.toLocaleString('en-US', {minimumFractionDigits: 2})}
                  </span>
                </div>
                <div className="strategy-stat">
                  <span className="stat-label">Positions</span>
                  <span className="stat-value">{strategyData.strategies.momentum.positions_count}</span>
                </div>
                <div className="strategy-stat">
                  <span className="stat-label">Current Value</span>
                  <span className="stat-value">
                    ${strategyData.strategies.momentum.total_value.toLocaleString('en-US', {minimumFractionDigits: 2})}
                  </span>
                </div>
                <div className="strategy-stat">
                  <span className="stat-label">Unrealized P&L</span>
                  <span className={`stat-value ${strategyData.strategies.momentum.unrealized_pnl >= 0 ? 'positive' : 'negative'}`}>
                    {strategyData.strategies.momentum.unrealized_pnl >= 0 ? '+' : ''}
                    ${strategyData.strategies.momentum.unrealized_pnl.toFixed(2)}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Equity Curve */}
      <div className="chart-container">
        <div className="chart-header">
          <h2>Equity Curve (30 Days)</h2>
        </div>
        {history.length === 0 ? (
          <div className="no-chart-data">
            <p>No trading history yet. The equity curve will appear after your first trades.</p>
          </div>
        ) : (
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={history}>
              <CartesianGrid strokeDasharray="3 3" stroke="#1a2332" />
              <XAxis dataKey="date" stroke="#8b92a7" />
              <YAxis stroke="#8b92a7" />
              <Tooltip
                contentStyle={{ background: '#0f1729', border: '1px solid #1a2332', borderRadius: '8px' }}
                labelStyle={{ color: '#8b92a7' }}
              />
              <Line type="monotone" dataKey="equity" stroke="#00d4ff" strokeWidth={2} dot={false} />
            </LineChart>
          </ResponsiveContainer>
        )}
      </div>

      {/* Current Positions */}
      <div className="positions-section">
        <h2>Open Positions {selectedStrategy !== 'all' && `- ${STRATEGY_NAMES[selectedStrategy]}`}</h2>
        {filteredPositions.length === 0 ? (
          <p className="no-data">No open positions</p>
        ) : (
          <div className="positions-table">
            <div className="table-header">
              <div>Symbol</div>
              <div>Strategy</div>
              <div>Qty</div>
              <div>Entry</div>
              <div>Current</div>
              <div>P&L</div>
              <div>P&L %</div>
            </div>
            {filteredPositions.map((pos) => {
              const strategy = getSymbolStrategy(pos.symbol);
              return (
                <div key={pos.symbol} className="table-row">
                  <div className="symbol">{pos.symbol}</div>
                  <div style={{color: strategyColors[strategy], fontSize: '12px', fontWeight: '500'}}>
                    {STRATEGY_NAMES[strategy]}
                  </div>
                  <div>{pos.qty}</div>
                  <div>${pos.avg_entry_price.toFixed(2)}</div>
                  <div>${pos.current_price.toFixed(2)}</div>
                  <div className={pos.unrealized_pl >= 0 ? 'positive' : 'negative'}>
                    ${pos.unrealized_pl.toFixed(2)}
                  </div>
                  <div className={pos.unrealized_plpc >= 0 ? 'positive' : 'negative'}>
                    {(pos.unrealized_plpc * 100).toFixed(2)}%
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default Overview;